import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ColumnSpan } from 'src/app/shared/form/columnSpan.model';
import {
    FormComponent,
    IFormDialogData,
} from 'src/app/shared/form/form.component';
import {
    ArrayFormField,
    ButtonFormField,
    CheckboxFormField,
    FormField,
    SingleSelectionFormField,
    TextFormField,
    ToggleFormField,
} from 'src/app/shared/form/formField.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { ICrudService } from 'src/app/shared/grid/crud.service';
import { Lookup } from 'src/app/models/lookup.model';
import { Tenant } from 'src/app/models/tenant.model';
import {
    TenantFormModel,
    TenantRegion,
} from 'src/app/models/tenantDetail.model';
import { TenantService } from 'src/app/services/tenant.service';
import { REGION_CODE_LABELS } from 'src/app/shared/constants/regionCode.constants';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Bridges the non-CrudService {@link TenantService} to the {@link ICrudService} contract that
 * {@link FormComponent} expects. `getById`/`create`/`update` map the dialog view model to the residency
 * tenant endpoints; the remaining CRUD ops are never reached from this dialog.
 */
export class TenantFormCrudAdapter implements ICrudService<TenantFormModel> {
    constructor(private readonly tenantService: TenantService) {}

    getById(id: string): Observable<TenantFormModel> {
        return this.tenantService.getTenant(id).pipe(
            map((tenant) => ({
                id: tenant.id,
                name: tenant.name,
                email: '',
                isDistributed: tenant.isDistributed,
                regions: tenant.regions ?? [],
            })),
        );
    }

    create(payload: TenantFormModel): Observable<TenantFormModel> {
        return this.tenantService
            .createTenant({ name: payload.name, email: payload.email })
            .pipe(map((tenant: Tenant) => ({ ...payload, id: tenant.id })));
    }

    update(payload: TenantFormModel): Observable<Object> {
        return this.tenantService.updateTenant(payload.id, {
            name: payload.name,
            isDistributed: payload.isDistributed,
            regions: payload.isDistributed ? payload.regions : null,
        });
    }

    getAll(): Observable<Lookup[]> {
        throw new Error('TenantFormCrudAdapter.getAll is not supported');
    }

    patch(): Observable<Object> {
        throw new Error('TenantFormCrudAdapter.patch is not supported');
    }

    delete(): Observable<Object> {
        throw new Error('TenantFormCrudAdapter.delete is not supported');
    }

    createRange(): Observable<Object> {
        throw new Error('TenantFormCrudAdapter.createRange is not supported');
    }
}

@Component({
  standalone: false,
    selector: 'app-tenant-dialog',
    templateUrl: './../../shared/form/form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./../../shared/form/form.component.scss'],
})
export class TenantDialogComponent
    extends FormComponent<TenantFormModel>
    implements OnInit
{
    private regionOptions: Lookup[] = [];

    constructor(
        dialogRef: MatDialogRef<TenantDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IFormDialogData,
        tenantService: TenantService,
        protected injector: Injector,
    ) {
        super(
            dialogRef,
            new TenantFormCrudAdapter(tenantService),
            data,
            'Tenant',
            injector,
        );
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        this.regionOptions = Object.entries(REGION_CODE_LABELS).map(
            ([id, name]) => ({ id, name }),
        );
        this.getEntity().subscribe({
            next: (entity) => {
                this.entity = entity;
                this.entity.regions = this.entity.regions ?? [];
                this.initFields();
                this.isFormLoading = false;
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
        });
    }

    getNewObject(): TenantFormModel {
        return {
            name: '',
            email: '',
            isDistributed: false,
            regions: [],
        };
    }

    initFields(): void {
        this.fields = [
            new TextFormField({
                property: nameOf<TenantFormModel>((x) => x.name),
                name: 'Name',
                columnSpan: new ColumnSpan(1, 1),
                required: true,
            }),
            new TextFormField({
                property: nameOf<TenantFormModel>((x) => x.email),
                name: 'Email',
                columnSpan: new ColumnSpan(1, 1),
                required: this.isAdd,
                isVisible: () => this.isAdd,
            }),
            new ToggleFormField({
                property: nameOf<TenantFormModel>((x) => x.isDistributed),
                name: 'Distributed',
                columnSpan: new ColumnSpan(1, 1),
                required: false,
                isVisible: () => !this.isAdd,
                onChange: () => this.onDistributedChange(),
            }),
            new ButtonFormField({
                property: nameOf<TenantFormModel>((x) => x.id),
                name: 'Add Region',
                iconClass: 'fa fa-plus',
                columnSpan: new ColumnSpan(1, 1),
                required: false,
                isVisible: (entity: TenantFormModel) =>
                    !this.isAdd && entity.isDistributed,
                onClick: () => this.addRegion(),
            }),
            new ArrayFormField({
                property: nameOf<TenantFormModel>((x) => x.regions),
                name: 'Regions',
                items: this.buildRegionRows(),
                cssClass: 'multiple-rows',
                columnSpan: new ColumnSpan(1, 1),
                required: false,
                isVisible: (entity: TenantFormModel) =>
                    !this.isAdd && entity.isDistributed,
            }),
        ];
    }

    validate(): boolean {
        if (!this.entity.name?.trim()) {
            this.toastService.showError('', 'Name is required');
            return false;
        }

        if (this.isAdd) {
            if (!EMAIL_PATTERN.test(this.entity.email?.trim() ?? '')) {
                this.toastService.showError('', 'A valid email is required');
                return false;
            }
            return true;
        }

        if (this.entity.isDistributed) {
            const regions = this.entity.regions ?? [];
            if (regions.length === 0) {
                this.toastService.showError('', 'Add at least one region');
                return false;
            }
            if (regions.some((region) => !region.regionCode)) {
                this.toastService.showError('', 'Region code is required');
                return false;
            }
            const codes = regions.map((region) =>
                region.regionCode.toUpperCase(),
            );
            if (new Set(codes).size !== codes.length) {
                this.toastService.showError(
                    '',
                    'Region codes must be unique',
                );
                return false;
            }
            if (!regions.some((region) => region.isAuthoring)) {
                this.toastService.showError(
                    '',
                    'Select at least one authoring region',
                );
                return false;
            }
        }

        return true;
    }

    addRegion(): void {
        const regionsField = this.getRegionsField();
        if (!regionsField) return;
        this.entity.regions.push({
            regionCode: '',
            isAuthoring: !this.entity.regions.some(
                (region) => region.isAuthoring,
            ),
        });
        regionsField.items.push(this.createRegionRow());
        this.updateFieldVisibility();
    }

    removeRegion(row: TenantRegion): void {
        const index = this.entity.regions.indexOf(row);
        if (index < 0) return;
        this.entity.regions.splice(index, 1);
        this.getRegionsField()?.items.splice(index, 1);
        this.updateFieldVisibility();
    }

    private onDistributedChange(): void {
        if (this.entity.isDistributed && this.entity.regions.length === 0) {
            this.addRegion();
        }
    }

    private getRegionsField(): FormField | undefined {
        return this.fields?.find(
            (field) =>
                field.property === nameOf<TenantFormModel>((x) => x.regions),
        );
    }

    private buildRegionRows(): FormField[][] {
        return this.entity.regions.map(() => this.createRegionRow());
    }

    private createRegionRow(): FormField[] {
        return [
            new SingleSelectionFormField({
                property: nameOf<TenantRegion>((x) => x.regionCode),
                name: 'Region',
                options: this.regionOptions,
                columnSpan: new ColumnSpan(2, 4),
                cssClass: 'multiple-row',
                required: true,
            }),
            new CheckboxFormField({
                property: nameOf<TenantRegion>((x) => x.isAuthoring),
                name: 'Authoring',
                columnSpan: new ColumnSpan(1, 4),
                cssClass: 'multiple-row',
                required: false,
            }),
            new ButtonFormField({
                property: nameOf<TenantRegion>((x) => x.id),
                name: '',
                iconClass: 'fa fa-trash',
                columnSpan: new ColumnSpan(1, 4),
                cssClass: 'multiple-row',
                required: false,
                onClick: (row: TenantRegion) => this.removeRegion(row),
            }),
        ];
    }
}
